<template>
  <div>
    <canvas
      class="three-canvas"
      :class="{'pointer-cursor': cursorIsPointer, 'in-foreground': inForeground, 'on-the-side': detailsPanelConcept !== null}"
      @touchmove.prevent="preventEvent"
      ref="threeCanvas"
    ></canvas>

    <!-- <canvas ref="totemOutput" style="width: 1200px; height: 627px; position: fixed; top: 0; left: 0;"></canvas> -->
  </div>
</template>

<script>
import Viewport from '@/mixins/Viewport'
import Platform from '@/platform'
import HopeTotem from '@/atoms/HopeTotem'
import CameraController from '@/atoms/CameraController'
import HopeConceptStone from '@/atoms/HopeConceptStone'
import HopeStoneTexture from '@/atoms/HopeStoneTexture'
import ElasticNumber from '@/utils/ElasticNumber'
import RAF from '@/utils/RAF'
import MouseWheelInertiaDetector from '@/utils/MouseWheelInertiaDetector'
import Gyroscope from '@/utils/Gyroscope'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import delay from 'lodash/delay'
import anime from 'animejs'
import normalizeWheel from '@/utils/normalizeWheel'
import applyToonLighting from '@/utils/applyToonLighting'
import GoogleHopeService from '@/services/GoogleHopeService'

import cssVars from '@/_variables.scss'

/* global THREE */
import 'three/OrbitControls'

export default {
  name: 'TotemCanvas',

  mixins: [Viewport],

  props: {

    /*

    array of

    {
      position: {x, z}
      stones: [
        {
          color: Number
          label : String
          concepts: [id, id, id, ...]
        }, ...
      ]
    }, ...

    */
    totems: {
      type: Array,
      required: true
    },
    // one of totem OR wheel
    mode: {
      type: String,
      required: true,
      default: 'wheel'
    },
    blockedResize: {
      type: Boolean,
      required: false,
      defualt: false
    },
    // stones rotating in the wheel
    wheelConcepts: {
      type: Array,
      required: false
    },
    // concepts selected by the user
    selectedConcepts: {
      type: Array,
      required: false
    },
    // currently selected totem
    currentTotem: {
      type: Object,
      required: false
    },
    wheelBlock: {
      type: Boolean,
      required: false
    },
    detailsPanelConcept: {
      required: true
    },
    multiTotem: {
      required: false
    },
    explodeTotem: {
      required: false
    },
    // dragHintVisible: {
    //   type: Boolean,
    //   required: false
    // },
    tooltips: {
      type: Array,
      required: false
    },
    inactivityTime: {
      type: Number,
      required: false
    },
    shareOpenAlpha: {
      type: Number,
      required: false,
      default: 0 // 0 - 1
    }
  },

  data: function () {
    return {
      mouse: new THREE.Vector2(),
      mouseDown: new THREE.Vector2(),
      // mouseOnMultitotemAlpha0: new THREE.Vector2(),
      hasMouseOverStone: null,
      inForeground: false,
      animatingTopStones: false,
      currentTotemExploded: false,
      animatingWidth: false,
      finalModalShow: false,
      compareModePassed: false,
      takingScreenshot: false,
      isMouseDown: false,
      isMouseWheelActive: false,
      elasticWheel: new ElasticNumber(0),
      doScreenshot: false, // set manually to true if you want the scene to be ready for a screenshot of background totems
      gyroscope: Gyroscope
    }
  },

  mounted: function () {
    this._wheelConceptStones = []
    this._totems = []
    this._fixedTopConceptStones = []
    this._fixedCenteredConceptStone = null
    this._explodedStone = null
    this._backgroundTotemPlanes = []
    this._backgroundTotemsOffset = new ElasticNumber(0) // from -x to +x

    this._mouseWheelInertiaDetector = new MouseWheelInertiaDetector()
    this._wheelFogNear = 50
    this._wheelFogFar = 60

    this._singleTotemFogNear = 10
    this._singleTotemFogFar = 15

    this._multiTotemFogNear = 20
    this._multiTotemFogFar = 55

    this._elasticFogNear = new ElasticNumber(this._wheelFogNear)
    this._elasticFogFar = new ElasticNumber(this._wheelFogFar)

    if (!Platform.prerenderer) {
      this._renderer = new THREE.WebGLRenderer({
        antialias: true,
        canvas: this.$refs.threeCanvas,
        alpha: true,
        gammaFactor: 10,
        gammaInput: true,
        gammaOutput: true,
        preserveDrawingBuffer: true
      })
      this._raycaster = new THREE.Raycaster()
      this._clock = new THREE.Clock()
      // this._renderer.toneMapping = THREE.LinearToneMapping
      this._renderer.setClearColor(0x000000, 0)
      this._renderer.setPixelRatio(window.devicePixelRatio)
      this._renderer.setSize(this.viewPort.width, this.viewPort.height)
      // this._renderer.shadowMap.enabled = true // !this.doScreenshot
      // this._renderer.shadowMap.type = THREE.PCFSoftShadowMap
      this._renderer.autoClear = false

      this._totemScene = new THREE.Scene()
      this._totemCamera = new THREE.PerspectiveCamera(50, this.viewPort.width / this.viewPort.height, 1, 500)
      this._totemCamera.position.z = 8
      this._totemCamera.position.y = 1.3
      this._totemCamera.rotation.x = -0.02
      this._totemScene.add(this._totemCamera)

      if (!this.doScreenshot) {
        this._totemScene.fog = new THREE.Fog(new THREE.Color(cssVars.cBackground).getHex(), this._singleTotemFogNear, this._singleTotemFogFar)
      }

      let shadowMat = new THREE.MeshBasicMaterial()
      // let shadowMat = new THREE.ShadowMaterial({opacity: 0.1})
      // if (!this.doScreenshot) {
      //   shadowMat.onBeforeCompile = function (mat) {
      //     mat.fragmentShader = mat.fragmentShader.replace('#include <fog_fragment>', 'float fogFactor = smoothstep( fogNear, fogFar, fogDepth );\n gl_FragColor.a *= 1.0 - fogFactor;')
      //   }
      // }

      this._totemGround = new THREE.Mesh(new THREE.PlaneBufferGeometry(100, 100), shadowMat)
      this._totemGround.rotation.x = -Math.PI / 2
      this._totemGround.position.y = -0.5
      this._totemGround.visible = false
      // this._totemGround.receiveShadow = true
      this._totemScene.add(this._totemGround)

      this._totemLight = new THREE.DirectionalLight()
      // this._totemLight.castShadow = true
      // this._totemLight.shadow.mapSize.width = 512
      // this._totemLight.shadow.mapSize.height = 512
      // this._totemLight.shadow.camera.far = 200
      // this._totemLight.shadow.camera.left = -20
      // this._totemLight.shadow.camera.right = 25
      // this._totemLight.shadow.camera.top = 20
      // this._totemLight.shadow.camera.bottom = -7
      this._totemLight.position.set(20, 30, 20)

      this._totemScene.add(this._totemLight)

      this._singleTotemRadius = this.doScreenshot ? this._totemCamera.position.z * 2 : this._totemCamera.position.z
      this._singleTotemTarget = new THREE.Vector3(0, this._totemCamera.position.y, 0)
      this._singleTotemTheta = -0.1

      this._multiTotemRadius = this._singleTotemRadius + 2
      this._multiTotemTarget = new THREE.Vector3(0, this._singleTotemTarget.y + 3, 2)
      this._multiTotemTheta = -0.5

      // this.multiTotem = 0
      this._downMultiTotemAlpha = 0
      this._downShareOpenAlpha = 0
      this._explodeTotemAlpha = 0

      this._controls = new CameraController(this._totemCamera, this.viewPort)
      this._controls.radius = this._singleTotemRadius
      this._controls.target.copy(this._singleTotemTarget)
      this._controls.theta = this._singleTotemTheta
      this._controls.thetaMax = 0.1
      this._controls.phiMin = -0.5
      this._controls.phiMax = 0.5
      this._controls.mouseEnabled = false

      // create background planes
      if (!this.doScreenshot) {
        for (let i = 0; i < 3; i++) {
          let numPlanes = 15 * (i + 1)
          let middle = (numPlanes - 1) / 2
          let spacing = 5
          for (let k = 0; k < numPlanes; k++) {
            let plane = this.createBackgroundTotemPlane()
            plane.position.z = -20 - (10 * i) - (Math.random() * 7)
            plane.position.x = spacing * (k - middle) + ((0.5 - Math.random()) * 2)
            // plane.rotation.y = Math.random() < 0.5 ? 0 : Math.PI
            // plane.material.side = plane.rotation.y === 0 ? THREE.FrontSide : THREE.BackSide
            plane.visible = false
            this._totemScene.add(plane)
            this._backgroundTotemPlanes.push(plane)
          }
        }
      }

      // wheel scene
      this._wheelScene = new THREE.Scene()
      this._wheelLight = new THREE.DirectionalLight()
      // this._wheelLight.castShadow = true
      // this._wheelLight.shadow.mapSize.width = 512
      // this._wheelLight.shadow.mapSize.height = 512
      // this._wheelLight.shadow.camera.far = 5
      // this._wheelLight.shadow.camera.left = -6
      // this._wheelLight.shadow.camera.right = 6
      // this._wheelLight.shadow.camera.top = 6
      // this._wheelLight.shadow.camera.bottom = -6
      this._wheelLight.position.set(0, 0, 5)
      this._wheelScene.add(this._wheelLight)

      // let wheelShadowPlane = new THREE.Mesh(new THREE.PlaneBufferGeometry(8, 8), new THREE.ShadowMaterial({opacity: 0.1}))
      // wheelShadowPlane.position.z = 0.8
      // wheelShadowPlane.receiveShadow = true
      // this._wheelScene.add(wheelShadowPlane)

      this._wheelScene.position.set(0, 0, -5)
      this._totemCamera.add(this._wheelScene)

      this._tooltipStones = []

      this.updateMode(this.mode)
      this.onResize()
      delay(this.onResize, 1200)

      if (!this.$root.platform.mobile) {
        document.addEventListener('mousedown', this.onMouseDown)
        document.addEventListener('mousemove', this.onMouseMove)
        document.addEventListener('mouseup', this.onMouseUp)
        document.addEventListener('mousewheel', this.onMouseWheel)
        document.addEventListener('DOMMouseScroll', this.onMouseWheel)
      } else {
        document.addEventListener('touchstart', this.onMouseDown)
        document.addEventListener('touchmove', this.onMouseMove)
        document.addEventListener('touchend', this.onMouseUp)
      }

      RAF.add(this.render)
    }
  },

  beforeDestroy: function () {
    document.removeEventListener('mousedown', this.onMouseDown)
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
    document.removeEventListener('touchstart', this.onMouseDown)
    document.removeEventListener('touchmove', this.onMouseMove)
    document.removeEventListener('touchend', this.onMouseUp)
    document.removeEventListener('mousewheel', this.onMouseWheel)
    document.removeEventListener('DOMMouseScroll', this.onMouseWheel)
    this._wheelScene.traverse(this.destroySceneChildren)
    this._totemScene.traverse(this.destroySceneChildren)
    this._renderer.dispose()
    this._renderer.forceContextLoss()
    RAF.remove(this.render)
  },
  computed: {
    canvasWidth: function () {
      // return this.detailsPanelConcept && this.viewPort.width > parseInt(cssVars.bpSm) ? this.viewPort.width / 2 : this.viewPort.width
      return this.viewPort.width
    },
    canvasCenterPoint: function () {
      return this.detailsPanelConcept && this.viewPort.width > parseInt(cssVars.bpSm) ? 0.5 : 1
    },
    importanceSelectionStoneTrackWidth: function () {
      if (this.viewPort.width <= parseInt(cssVars.bpSm)) {
        return this.viewPort.width * (parseInt(cssVars.conceptSelectorMobileTrackPercent) / 100)
      }
      return this.viewPort.width * (parseInt(cssVars.conceptSelectorDesktopTrackPercent) / 100)
    },
    importanceSelectionStoneTrackY: function () {
      let halfHeight = this.viewPort.height / 2
      let desktopY = (-(halfHeight - (parseInt(cssVars.conceptSelectorDesktopHeight) / 2))) / halfHeight
      // -1 : halfHeight = x : halfHeight - 200
      return this.viewPort.width <= parseInt(cssVars.bpSm) ? -0.3 : desktopY
    },
    cursorIsPointer: function () {
      return this.hasMouseOverStone
    }
  },

  methods: {
    preventEvent: function () {},
    createBackgroundTotemPlane: function () {
      let geometry = new THREE.PlaneBufferGeometry(4, 4)
      geometry.translate(0, 1.5, 0)
      let material = new THREE.MeshBasicMaterial({
        transparent: true,
        // color: 0xf7f7ef,
        map: new THREE.TextureLoader().load(`/static/img/totem${Math.floor(Math.random() * 9)}.png`),
        depthWrite: false
      })
      // if (!this.doScreenshot) {
      //   material.onBeforeCompile = function (mat) {
      //     mat.fragmentShader = mat.fragmentShader.replace('#include <fog_fragment>', 'float fogFactor = smoothstep( fogNear, fogFar, fogDepth );\n gl_FragColor.a *= 1.0 - fogFactor;')
      //   }
      // }
      return new THREE.Mesh(geometry, material)
    },

    destroySceneChildren: function (value) {
      if (typeof value.destroy === 'function') {
        // console.log('destroy')
        value.destroy()
      } else {
        if (value.geometry) {
          value.geometry.dispose()
        }
        if (value.material) {
          if (value.material.map) {
            value.material.map.dispose()
          }
          value.material.dispose()
        }
      }
    },

    setTotemAllStonesExplodedState: function (value, totem) {
      let totemObject = this.getTotem(totem)
      if (totemObject) {
        this.currentTotemExploded = value
        totemObject.allStonesExploded = value
        for (let i = 0; i < totemObject._stones.length; i++) {
          totemObject._stones[i].exploded = value
        }
      }
      if (!value) {
        this.$emit('update:detailsPanelConcept', null)
      }
      this.$emit('update:explodeTotem', value ? 1 : 0)

      if (totemObject.config.id === this.currentTotem.id) {
        this._forceLabelsOpen = true
        clearTimeout(this._forceLabelsOpenDelay)
        this._forceLabelsOpenDelay = delay(() => {
          this._forceLabelsOpen = false
          this._tooltipStones = []
        }, 4000)

        if (totemObject.allStonesExploded === false) {
          this._tooltipStones = totemObject._stones
        } else {
          let stones = []
          totemObject._stones.forEach((stone) => {
            stones = stones.concat(stone._explodedStones)
          })
          this._tooltipStones = stones
        }
      } else {
        this._tooltipStones = []
      }
    },

    updateMode: function (value) {
      this.isMouseDown = false
      switch (value) {
        case 'totem':
          if (this._explodedStone) {
            this._explodedStone.exploded = false
            this._explodedStone = null
          }
          this._lastInteractionTime = this._clock.elapsedTime

          // this.multiTotem = 0
          this.$emit('update:multiTotem', 0)
          this._backgroundTotemsOffset.target = this._backgroundTotemsOffset.value = 0
          if (this._fixedTopConceptStones.length === 0) {
            this._elasticFogNear.value = this._elasticFogNear.target = this.lerp(this._singleTotemFogNear, this._multiTotemFogNear, this.multiTotem)
            this._elasticFogFar.value = this._elasticFogFar.target = this.lerp(this._singleTotemFogFar, this._multiTotemFogFar, this.multiTotem)
            this.updateTotems(this.totems)
          } else {
            this.animateTopConceptStones()
          }
          break
        case 'wheel':
          if (this._explodedStone) {
            this._explodedStone.exploded = false
            this._explodedStone = null
          }
          this.updateWheelConcepts(this.wheelConcepts)
          this.updateSelectedConcepts(this.selectedConcepts)
          this.syncTopStones()
          this._elasticFogNear.value = this._elasticFogNear.target = this._wheelFogNear
          this._elasticFogFar.value = this._elasticFogFar.target = this._wheelFogFar
          break
      }
    },
    syncTopStones: function () {
      this.wheelConcepts.forEach(config => {
        if (config.concept.confirmed) {
          let existingTopStone = find(this._fixedTopConceptStones, {config: {id: config.concept.id}})
          if (!existingTopStone) {
            // let cat = GoogleHopeService.getConceptCategory(config.concept)
            let top = new HopeConceptStone(config.concept, new THREE.MeshToonMaterial({
              specular: new THREE.Color(0x000000),
              shininess: 0,
              map: new HopeStoneTexture(config.concept.categoryObject.color, 1),
              transparent: false
            }))
            top.material.onBeforeCompile = applyToonLighting
            top.elasticPosition.speed = 4
            top.setVisible(true)
            top.castShadow = false
            this._fixedTopConceptStones.push(top)
            this._totemCamera.add(top)
            this.calculateFixedTopConceptStones()
          }
        }
      })
    },
    updateWheelConcepts: function (value) {
      // remove stones
      for (let i = this._wheelConceptStones.length - 1; i >= 0; i--) {
        let concept = this._wheelConceptStones[i]
        if (!find(value, {concept: {id: concept.config.id}})) {
          this._wheelScene.remove(concept)
          concept.destroy()
          this._wheelConceptStones.splice(i, 1)
        }
      }

      let pos = new THREE.Vector3()

      // add / update stones
      for (let i = 0; i < value.length; i++) {
        let concept = value[i]
        let existingConcept = find(this._wheelConceptStones, {config: {id: concept.concept.id}})
        if (!existingConcept) {
          let mesh = new HopeConceptStone(concept.concept, new THREE.MeshToonMaterial({
            specular: new THREE.Color(0x000000),
            shininess: 0,
            map: new HopeStoneTexture(concept.concept.categoryObject.color, 1),
            transparent: false
          }))
          mesh.material.onBeforeCompile = applyToonLighting
          mesh.addWheelShadowPlane()
          mesh.setVisible(true)
          this._wheelConceptStones.push(mesh)
          this._wheelScene.add(mesh)
        } else {
          pos.x = concept.x
          pos.y = -concept.y
          pos.z = 0.5
          pos.unproject(this._totemCamera)
          // let pos = new THREE.Vector3(concept.x, -concept.y, 0.5).unproject(this._totemCamera)

          // pos.multiplyScalar(0.99)
          existingConcept.position.copy(this._wheelScene.worldToLocal(pos))
          existingConcept.rotation.z = -concept.t
        }
      }
    },
    animateTopConceptStones: function () {
      this.animatingTopStones = true
      this.updateTotems(this.totems)

      let targetTotem = this._totems[0]
      targetTotem.updateMatrixWorld(true)
      targetTotem.update(1 / 60)
      targetTotem.doUpdate = false
      targetTotem.label.visible = false

      let index = 0
      while (this._fixedTopConceptStones.length > 0) {
        let stone = this._fixedTopConceptStones[0]
        this._fixedTopConceptStones.splice(0, 1)
        stone.position.copy(this._totemCamera.localToWorld(stone.position))
        this._totemScene.add(stone)
        let target = this.findTopStoneAnimationTarget(stone)
        let endPosition = target.getWorldPosition()
        let isLast = this._fixedTopConceptStones.length === 0
        // draw 0 lines to target
        // target.exploded = false
        target.setScale(false, false)
        target.scale.set(0.00001, 0.00001, 0.00001)
        target.material.map.draw(target.config.color, 0)
        target.inAnimatedStonesNum = 0
        anime({
          targets: stone.position,
          x: endPosition.x,
          y: endPosition.y,
          z: endPosition.z,
          duration: 500,
          easing: 'easeOutQuart',
          delay: 200 * (index + 1),
          complete: (anim) => {
            target.inAnimatedStonesNum++
            target.cancelScaleAnimation()
            //
            target.animateScale(0.3 + (target.inAnimatedStonesNum / 10))
            //
            target.material.map.draw(target.config.color, target.inAnimatedStonesNum)
            this._totemScene.remove(stone)
            stone.destroy()
            if (isLast) {
              this.onAnimateTopConceptStonesEnd(targetTotem)
            }
          }
        })
        index++
      }
    },
    dataURItoBlob: function (dataURI) {
      // https://stackoverflow.com/questions/4998908/convert-data-uri-to-file-then-append-to-formdata/9010182
      var byteString
      if (dataURI.split(',')[0].indexOf('base64') >= 0) {
        byteString = atob(dataURI.split(',')[1])
      } else {
        byteString = unescape(dataURI.split(',')[1])
      }
      var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
      var ia = new Uint8Array(byteString.length)
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      return new Blob([ia], {type: mimeString})
    },
    onAnimateTopConceptStonesEnd: function (totem) {
      this._elasticFogNear.value = this._elasticFogNear.target = this.lerp(this._singleTotemFogNear, this._multiTotemFogNear, this.multiTotem)
      this._elasticFogFar.value = this._elasticFogFar.target = this.lerp(this._singleTotemFogFar, this._multiTotemFogFar, this.multiTotem)
      this.animatingTopStones = false
      totem.doUpdate = true

      this._forceLabelsOpen = true
      clearTimeout(this._forceLabelsOpenDelay)
      this._forceLabelsOpenDelay = delay(() => {
        this._forceLabelsOpen = false
        this._tooltipStones = []
      }, 4000)

      this.takingScreenshot = true

      delay(() => {
        // let screenshot = this.getScreenshot('image/png')
        this.getScreenshot('image/jpeg', 1200, 627, (screenshot) => {
          GoogleHopeService.saveThumbnail(this.totems[0].id, this.dataURItoBlob(screenshot))
          this.takingScreenshot = false

          this._totems.forEach((totem) => {
            totem.label.enable()
          })
        })

        // console.log(GoogleHopeService.saveThumbnail)
        // console.log(screenshot)
        // window.open(screenshot, '_blank')
      }, 2500)

      // this._lastInteractionTime = this._clock.elapsedTime
      this.$emit('totemReady')

      this._tooltipStones = this.getTotem(this.currentTotem)._stones
      // totem.config = totem.config
      this.updateTotems(this.totems)
    },
    findTopStoneAnimationTarget: function (stone) {
      let ret = null
      this._totems.some((totem, i) => {
        let categoryStone = totem.findCategoryStoneByConcept(stone.config)
        if (categoryStone) {
          ret = categoryStone.unexplodedStone
          return true
        }
        return false
      })
      return ret
    },
    updateTotems: function (value) {
      // console.log('update totems')
      // remove totems
      for (let i = this._totems.length - 1; i >= 0; i--) {
        let totem = this._totems[i]
        if (!find(value, {id: totem.config.id})) {
          this._totemScene.remove(totem)
          totem.destroy()
          this._totems.splice(i, 1)
          // console.log('removed totem', totem)
        }
      }

      // add/update totems
      value.forEach((totemData, index) => {
        let existingTotem = this.getTotem(totemData)
        if (!existingTotem) {
          // console.log('created totem', totemData)

          let totem = {}

          if (index === 0) { // user totem
            totem = new HopeTotem(totemData, true)
          } else {
            totem = new HopeTotem(totemData, false, this._totems)
          }

          if (this.doScreenshot) {
            totem.remove(totem.label)
          }
          totem.position.x = totemData.position.x
          totem.position.z = !this.doScreenshot ? totemData.position.z : 0
          totem.position.y = 0
          this._totemScene.add(totem)
          this._totems.push(totem)
        } else {
          // console.log('updated totem', totemData)
          existingTotem.config = totemData
        }
      })
      this.$emit('update:currentTotem', value[0])
    },

    setTotemsVisible: function (userTotemVisible, backTotemsVisible) {
      for (let i = 0; i < this._totems.length; i++) {
        if (i === 0) {
          this._totems[i].visible = userTotemVisible
        } else {
          this._totems[i].visible = this._totems[i].doUpdate = backTotemsVisible
        }
      }
      for (let i = 0; i < this._backgroundTotemPlanes.length; i++) {
        this._backgroundTotemPlanes[i].visible = backTotemsVisible
      }
    },

    currentTotemIsUserTotem: function () {
      return this.currentTotem ? this.currentTotem.id === this.totems[0].id : true
    },

    backgroundTotemsVisible: function () {
      let fogFar = 0
      if (!this.doScreenshot) {
        fogFar = this._totemCamera.position.z - this._totemScene.fog.far
      }
      // console.log(fogFar, this.totems[1])
      return this.doScreenshot || ((!this.animatingTopStones && fogFar < -8) || !this.currentTotemIsUserTotem())
      // return (!this.animatingTopStones && this._totemScene.fog.near >= this._singleTotemFogNear + 0.1) || !this.currentTotemIsUserTotem()
    },

    render: function () {
      let delta = this._clock.getDelta()

      this.elasticWheel.target = 0
      this.elasticWheel.update(delta)

      if (this.multiTotem === 1 && this.explodeTotem === 0) {
        this.compareModePassed = true
      }

      if (this._totems && this.currentTotem) {
        if (this.currentTotem.id === this._totems[0]._config.id) {
          if (this.multiTotem === 0 && this.explodeTotem === 0 && this.compareModePassed && !this.finalModalShow) {
            this.finalModalShow = true

            this.$emit('finalmodal:show')
          }
        }
      }

      if (this._controls) {
        this._explodeTotemAlpha = 0
        let currentTotem = this.getTotem(this.currentTotem)
        let newMultiTotem = this.multiTotem

        if (this.isMouseDown && this._downMultiTotemAlpha === 0 && newMultiTotem === 0) {
          this._explodeTotemAlpha = Math.max(-1, Math.min(1, -(this.mouseDown.y - this.mouse.y)))
        }

        if (this.isMouseWheelActive && this._downMultiTotemAlpha === 0 && newMultiTotem === 0 && !this._pauseWheelActions) {
          this._explodeTotemAlpha -= this.elasticWheel.value // * 1.5
          let threshold = 0.4
          if (this.elasticWheel.value < -threshold && this._explodeTotemAlpha > threshold && !currentTotem.allStonesExploded) {
            // console.log('wheel explode')
            this.setTotemAllStonesExplodedState(true, this.currentTotem)
            this._pauseWheelActions = true
            clearTimeout(this.pauseWheelActionsDelay)
            this.pauseWheelActionsDelay = delay(() => {
              this._pauseWheelActions = false
            }, 500)
            this.elasticWheel.value = this.elasticWheel.target = 0
          } else if (this.elasticWheel.value > threshold && this._explodeTotemAlpha < -threshold && currentTotem.allStonesExploded) {
            // console.log('wheel unexplode')
            this.elasticWheel.value = this.elasticWheel.target = 0
            this._pauseWheelActions = true
            clearTimeout(this.pauseWheelActionsDelay)
            this.pauseWheelActionsDelay = delay(() => {
              this._pauseWheelActions = false
            }, 500)
            this.setTotemAllStonesExplodedState(false, this.currentTotem)
          }
        }

        this._explodeTotemAlpha = Math.max(-1, Math.min(1, this._explodeTotemAlpha))
        if (currentTotem) currentTotem.spacing = Math.max(0, this._explodeTotemAlpha * 0.2)
        // console.log(this._explodeTotemAlpha)

        if (currentTotem && !currentTotem.allStonesExploded) {
          // we are on user totem and it isn't exploded

          let offsetLimit = (this.totems.length - 2) / 2
          if (this.isMouseDown) {
            // move alpha
            newMultiTotem = Math.max(0, Math.min(1, this._downMultiTotemAlpha + (this.mouseDown.y - this.mouse.y)))

            if (newMultiTotem > 0.5) {
              // move background totems offset
              this._backgroundTotemsOffset.target = Math.max(-offsetLimit, Math.min(offsetLimit, this._backgroundTotemsOffsetDown + (this.mouse.x - this.mouseDown.x) * 2))
            } else {
              this._backgroundTotemsOffset.target = Math.round(this._backgroundTotemsOffset.target)
            }
          }

          if (this.isMouseWheelActive && !this._pauseWheelActions) {
            newMultiTotem += this.elasticWheel.value
          }
        }

        if (!this.isMouseDown && !this.isMouseWheelActive) {
          newMultiTotem = Math.round(newMultiTotem)
          this._backgroundTotemsOffset.target = Math.round(this._backgroundTotemsOffset.target)
        }
        newMultiTotem = Math.max(0, Math.min(1, newMultiTotem))
        // console.log(this._controls._lookat.value.z, newMultiTotem)
        if (this._controls._lookat.value.z < -5 && newMultiTotem > 0.5) {
          // we should return to user totem
          this.$emit('update:currentTotem', this.totems[0])
          // this.multiTotem = 1
          // this.isMouseDown = false
        }

        let target = new THREE.Vector3()
        // lerps
        target.lerpVectors(this._singleTotemTarget, this._multiTotemTarget, newMultiTotem)
        // center around current totem
        if (currentTotem) target.add(currentTotem.position)
        this._controls.target.copy(target)

        // lerp
        this._controls.theta = this.lerp(this._singleTotemTheta, this._multiTotemTheta, newMultiTotem) + (this._explodeTotemAlpha * 0.2)
        this._controls.radius = this.lerp(this._singleTotemRadius, this._multiTotemRadius, newMultiTotem)

        if (!Platform.mobile) {
          if (!this.isMouseDown) {
            this._controls.phi = (-this._controls._mousePickingPosition.x * 0.25) * (1 - newMultiTotem)
            this._controls.theta += (this._controls._mousePickingPosition.y * 0.1) * (1 - newMultiTotem)
          } else {
            this._controls.phi = 0
          }
        } else {
          this._controls.phi = (this.gyroscope.orientationY * 0.008) * (1 - newMultiTotem)
          let rad = (this.gyroscope.orientationX * Math.PI) / 180
          this._controls.theta += ((rad - (Math.PI / 4)) * 0.2) * (1 - newMultiTotem)
        }

        if (newMultiTotem !== this.multiTotem) {
          this.$emit('update:multiTotem', newMultiTotem)
        }
        if (!this.animatingTopStones) {
          this._controls.update(delta)
        }
      }

      if (this.mode === 'totem') {
        if (Platform.mobile) this.gyroscope.update()
        this.handleTotemsUpdate(delta)

        if (!this.detailsPanelConcept) {
          this.$emit('update:inactivityTime', this._clock.elapsedTime - this._lastInteractionTime)
        } else {
          this._lastInteractionTime = this._clock.elapsedTime
        }

        if (this.isMouseDown && this.detailsPanelConcept == null && Platform.mobile) {
          let newShareOpenAlpha = Math.max(0, Math.min(1, this._downShareOpenAlpha + (this.mouseDown.x - this.mouse.x) * 3))
          newShareOpenAlpha = Math.max(0, Math.min(1, newShareOpenAlpha * Math.pow(1 - this.multiTotem, 6) * Math.pow(1 - Math.abs(this._explodeTotemAlpha), 6)))
          this.$emit('update:shareOpenAlpha', newShareOpenAlpha)
        } else {
          if (this.shareOpenAlpha !== Math.round(this.shareOpenAlpha)) {
            this.$emit('update:shareOpenAlpha', Math.round(this.shareOpenAlpha))
          }
        }

        let updatedToolTips = []
        if (this.multiTotem === 0) {
          for (let i = 0; i < this._tooltipStones.length; i++) {
            let stone = this._tooltipStones[i]
            updatedToolTips.push(Object.assign(this.getTooltipPosition(stone), stone.config))
          }
        }
        this.$emit('update:tooltips', updatedToolTips)

        // this.setTotemsVisible(false, true)
        this.setTotemsVisible(!this.doScreenshot, this.backgroundTotemsVisible())
        if (!this.animatingTopStones) {
          this._elasticFogNear.target = this.lerp(this._singleTotemFogNear, this._multiTotemFogNear, this.multiTotem)
          this._elasticFogFar.target = this.lerp(this._singleTotemFogFar, this._multiTotemFogFar, this.multiTotem)
        } else {
          // when animating top concept
          this._elasticFogNear.target = this._wheelFogNear
          this._elasticFogFar.target = this._wheelFogFar
        }
      } else {
        this.setTotemsVisible(false, false)
        this._elasticFogNear.target = this._wheelFogNear
        this._elasticFogFar.target = this._wheelFogFar
      }

      this._elasticFogNear.update(delta)
      this._elasticFogFar.update(delta)
      if (this._totemScene.fog) {
        this._totemScene.fog.near = this._elasticFogNear.value
        this._totemScene.fog.far = this._elasticFogFar.value
      }

      if (this.mode === 'wheel') {
        this.handleCenterStoneDragging()
      }

      this.inForeground = this._fixedCenteredConceptStone !== null
      // update top stones
      for (let i = 0; i < this._fixedTopConceptStones.length; i++) {
        this._fixedTopConceptStones[i].update(null, delta, true)
      }

      let wheelSceneVisible = this.viewPort.width <= parseInt(cssVars.bpSm) ? !this._fixedCenteredConceptStone : true

      this._wheelScene.visible = this.mode === 'wheel' && wheelSceneVisible
      // this._totemGround.visible = this._totemLight.visible = !this._wheelScene.visible
      this._totemLight.visible = !this._wheelScene.visible

      this._renderer.render(this._totemScene, this._totemCamera, null, true)
    },

    lerp: function (v0, v1, t) {
      return v0 + t * (v1 - v0)
    },

    handleTotemsUpdate: function (delta) {
      this._mouseOverStone = null
      this.hasMouseOverStone = false
      this._backgroundTotemsOffset.update(delta)
      if (this._totems) {
        // totem stones raycast picking
        this._raycaster.setFromCamera(this.mouse, this._totemCamera)

        for (let i = 0; i < this._totems.length; i++) {
          let totem = this._totems[i]
          if (totem.doUpdate) {
            totem.update(delta, this._totemGround)
            if (totem.visible) {
              this.handleTotemPicking(totem)
            }
          }
          // background totems scroll
          if (i > 0) {
            // totem.position.x = this.totems[i].position.x + (this._backgroundTotemsOffset.value * 5)

            // circle related totems
            let center = new THREE.Vector3(0, 0, 30)
            let span = Math.PI / 7
            let radius = 40
            // Math.PI / 2 : 0.4 = span : x
            let offsetAngle = (span / 2) + this._backgroundTotemsOffset.value * ((span * 0.4) / (Math.PI / 2))
            let angle = (span * (i - 1)) / (this.totems.length - 2)
            angle += Math.PI
            totem.position.x = center.x + radius * Math.sin(angle - offsetAngle)
            totem.position.z = center.z + radius * Math.cos(angle - offsetAngle)

            if (this.doScreenshot) {
              totem.position.x *= 0.55
            }
          }
        }
      }
    },

    handleTotemPicking: function (totem) {
      if (!this.hasMouseOverStone) {
        let picking = []
        for (let k = 0; k < totem.stones.length; k++) {
          let stone = totem.stones[k]
          if (!stone.exploded) {
            // raycast collision mesh of unexploded stones
            stone.collisionMesh.raycast(this._raycaster, picking)
            if (picking.length > 0) {
              this.hasMouseOverStone = true
              this._mouseOverStone = picking[0].object.parent.parent
              return true
            }
          } else {
            // stone is exploded
            // raycast exploded substones
            if (this.handleStonePicking(stone, picking)) {
              return true
            }
          }
        }
      }
      return false
    },
    handleStonePicking: function (stone, picking) {
      for (let j = 0; j < stone.explodedStones.length; j++) {
        let expStone = stone.explodedStones[j]
        expStone.collisionMesh.raycast(this._raycaster, picking)
        if (picking.length > 0) {
          this.hasMouseOverStone = true
          this._mouseOverStone = picking[0].object.parent
          return true
        }
      }
      return false
    },
    handleCenterStoneDragging: function () {
      this.hasMouseOverStone = false
      this._mouseOverStone = null
      // drag centered stone (importance)
      if (this._fixedCenteredConceptStone) {
        this._raycaster.setFromCamera(this.mouse, this._totemCamera)
        let picking = []
        this._fixedCenteredConceptStone.raycast(this._raycaster, picking)
        if (picking.length > 0) {
          this.hasMouseOverStone = true
          this._mouseOverStone = this._fixedCenteredConceptStone
        }

        if (this.isDraggingCenterStone) {
          // let trackWidth = this.viewPort.width <= 1099 ? this.viewPort.width / 2 : this.viewPort.width / 4
          let mouseMult = this.viewPort.width / (this.importanceSelectionStoneTrackWidth * 2)
          this._fixedCenteredConceptStone.config.importance = Math.max(0, Math.min(1, (this.mouse.x * mouseMult) + 0.5))
          this.calculateFixedCenteredConceptStone()
        }
      }
    },
    getTotem: function (totemData) {
      if (!totemData) {
        return null
      }
      return find(this._totems, {config: {id: totemData.id}})
    },
    onMouseDown: function (event) {
      if (this.recursiveDatasetSearch(event.target, 'blockInteraction')) return
      // if (this.recursiveDatasetSearch(event.target, 'blockInteraction') || this.detailsPanelConcept) return

      if (this._controls) {
        this._controls.onMouseDown(event)
      }
      // this.$emit('update:dragHintVisible', false)
      this._lastInteractionTime = this._clock.elapsedTime

      let x = event.touches && event.touches.length > 0 ? event.touches[0].clientX : event.clientX
      let y = event.touches && event.touches.length > 0 ? event.touches[0].clientY : event.clientY
      this.mouse.x = (x / this.canvasWidth) * 2 - this.canvasCenterPoint
      this.mouse.y = -(y / this.viewPort.height) * 2 + 1
      this.mouseDown.copy(this.mouse)
      // this.mouseOnMultitotemAlpha0.copy(this.mouse)
      this.isMouseDown = true
      this._downShareOpenAlpha = this.shareOpenAlpha
      this._downMultiTotemAlpha = this.multiTotem
      this._backgroundTotemsOffsetDown = this._backgroundTotemsOffset.target

      if (this.mode === 'wheel') {
        this.handleCenterStoneDragging()
        if (this._fixedCenteredConceptStone && this.hasMouseOverStone) {
          this.isDraggingCenterStone = true
        }
        // this.handleCenterStoneDragging()
      }
      // this._tooltipStone = null
      // this.$emit('update:tooltip', null)
    },
    onMouseUp: function (event) {
      if (this.takingScreenshot || this.wheelBlock) {
        this.isMouseDown = false

        return
      }

      if (this.recursiveDatasetSearch(event.target, 'blockInteraction')) return
      // if (this.recursiveDatasetSearch(event.target, 'blockInteraction') || this.detailsPanelConcept) return
      // event.preventDefault()
      if (this._controls) {
        this._controls.onMouseUp(event)
      }
      this._lastInteractionTime = this._clock.elapsedTime

      this.isDraggingCenterStone = false
      this.isMouseDown = false

      if (this._explodeTotemAlpha > 0.5 && !this.currentTotemExploded) {
        this.setTotemAllStonesExplodedState(true, this.currentTotem)
      } else if (this._explodeTotemAlpha < -0.5 && this.currentTotemExploded) {
        this.setTotemAllStonesExplodedState(false, this.currentTotem)
      }

      // this is not a click, skip the rest
      if (!this.isClick(this.mouse, this.mouseDown)) {
        this._tooltipStones = []
        return
      }

      // this is a click

      if (this.mode === 'totem') {
        if (this.doScreenshot) {
          this.saveScreenshot()
        }
        // let screenshot = this.getScreenshot('image/jpeg', 1200, 627)
        // console.log(screenshot)
        if (this.hasMouseOverStone) {
          // switch current totem
          if (!this.stoneIsPartOfTotem(this._mouseOverStone, this.currentTotem)) {
            this.$emit('update:currentTotem', this._mouseOverStone.parent.config)
          } else {
            if (!this._mouseOverStone.config.concepts) {
              // we clicked a concept stone
              // console.log('click on concept', this._mouseOverStone.config)
              this.$emit('concept:click', this._mouseOverStone.config)
              if (event.type === 'touchend') {
                this._tooltipStones = []
              }
            } else {
              // we clicked a category stone
              if (this.multiTotem !== 0) this.$emit('update:multiTotem', 0)
              if (event.type === 'mouseup') {
                // desktop event
                this._tooltipStones = this.getTotem(this.currentTotem)._stones
              } else if (event.type === 'touchend') {
                // mobile event on click on stone
                if (this._tooltipStones.length > 0 && this._tooltipStones[0] === this._mouseOverStone) {
                  // tapped on same cat stone 2 times
                  // this.$emit('update:dragHintVisible', true)
                } else {
                  // tapped on diffferent cat stone
                  this._tooltipStones = [this._mouseOverStone]
                }
              }
            }
          }
        } else {
          // click outside totem
          let currentTotem = this.getTotem(this.currentTotem)
          if (event.type === 'touchend') {
            let totem = this.getTotem(this.currentTotem)
            if (currentTotem.allStonesExploded) {
              let stones = []
              totem._stones.forEach((stone) => {
                stones = stones.concat(stone._explodedStones)
              })
              this._tooltipStones = stones
            } else {
              // disabled as per request?
              // this._tooltipStones = totem._stones
            }
          }
        }
      }
    },
    getTooltipPosition: function (stone) {
      let pos

      if (stone.config.concepts) {
        pos = stone.unexplodedStone.getWorldPosition()
        pos.x += stone.unexplodedStone.collisionGeometry.boundingSphere.radius * stone.unexplodedStone.scale.x
      } else {
        pos = stone.getWorldPosition()
        pos.x += stone.collisionGeometry.boundingSphere.radius * stone.scale.x
      }
      this._totemCamera.parent.worldToLocal(pos)
      pos = pos.project(this._totemCamera)
      pos.x = Math.round((pos.x + this.canvasCenterPoint) * this.viewPort.width / 2)
      pos.y = Math.round((-pos.y + 1) * this.viewPort.height / 2)
      pos.z = 0
      return pos
    },
    stoneIsPartOfTotem: function (stone, totem) {
      return stone.parent.config.stones ? stone.parent.config.id === totem.id : stone.parent.parent.config.id === totem.id
    },
    isClick: function (mouse, mouseDown) {
      return Math.abs(mouse.distanceTo(mouseDown)) < 0.01
    },
    onMouseMove: function (event) {
      if (this.takingScreenshot || this.wheelBlock) {
        return
      }

      if (this.recursiveDatasetSearch(event.target, 'blockInteraction')) return
      // if (this.recursiveDatasetSearch(event.target, 'blockInteraction') || this.detailsPanelConcept) return
      // event.preventDefault()

      if (this.isMouseDown) {
        this._lastInteractionTime = this._clock.elapsedTime
      }

      // only desktop mousemove
      if (event.type === 'mousemove' && this.mode === 'totem' && !this._forceLabelsOpen) {
        if (this._mouseOverStone && this.stoneIsPartOfTotem(this._mouseOverStone, this.currentTotem) && this.multiTotem === 0) {
          if (this._previousMouseOverStone !== this._mouseOverStone) {
            this._tooltipStones = [this._mouseOverStone]
          }
        } else {
          this._tooltipStones = []
        }
      }

      this._previousMouseOverStone = this._mouseOverStone

      if (this._controls) {
        this._controls.onMouseMove(event)
      }
      let x = event.touches && event.touches.length > 0 ? event.touches[0].clientX : event.clientX
      let y = event.touches && event.touches.length > 0 ? event.touches[0].clientY : event.clientY
      this.mouse.x = (x / this.canvasWidth) * 2 - this.canvasCenterPoint
      this.mouse.y = -(y / this.viewPort.height) * 2 + 1
    },
    onMouseWheel: function (event) {
      if (this.takingScreenshot || this.wheelBlock) {
        return
      }

      if (this.recursiveDatasetSearch(event.target, 'blockInteraction') || this.mode !== 'totem' || this.animatingTopStones) return
      event.preventDefault()
      let normalized = normalizeWheel(event)
      // console.log(normalized.pixelY, this._mouseWheelInertiaDetector.check(normalized))
      if (this._mouseWheelInertiaDetector.check(normalized) === false) return
      // if (Math.abs(delta) < 100) return
      let delta = normalized.pixelY
      this.elasticWheel.value += delta * 0.002
      this.elasticWheel.value = Math.max(-1, Math.min(1, this.elasticWheel.value))
      this._lastInteractionTime = this._clock.elapsedTime

      if (!this.isMouseWheelActive) {
        this._downMultiTotemAlpha = this.multiTotem
      }
      this.isMouseWheelActive = true
      clearTimeout(this.wheelActiveDelay)
      this.wheelActiveDelay = delay(() => {
        // if (this._explodeTotemAlpha > 0.5 && !this.currentTotemExploded) {
        //   this.setTotemAllStonesExplodedState(true, this.currentTotem)
        // } else if (this._explodeTotemAlpha < -0.5 && this.currentTotemExploded) {
        //   this.setTotemAllStonesExplodedState(false, this.currentTotem)
        // }
        this.isMouseWheelActive = false
      }, 200)
    },
    calculateFixedTopConceptStones: function () {
      const maxStones = 9
      // const middle = (this._fixedTopConceptStones.length - 1) / 2
      // width : 1 = 500 : x
      let desktopTotalSpace = parseInt(cssVars.conceptContainerDesktopWidth) / this.viewPort.width
      let mobileTotalSpace = 1 - ((parseInt(cssVars.conceptContainerMobileRight) + parseInt(cssVars.conceptContainerMobileLeft)) / this.viewPort.width) - 0.025

      let totalSpace = this.viewPort.width > parseInt(cssVars.bpSm) ? desktopTotalSpace : mobileTotalSpace

      const spacing = totalSpace / (maxStones / 2)

      const leftSide = this.viewPort.width > parseInt(cssVars.bpSm) ? -totalSpace : -(1 - (parseInt(cssVars.conceptContainerMobileRight) / this.viewPort.width)) - 0.025

      const halfHeight = (this.viewPort.height / 2)
      const y = (halfHeight - (parseInt(cssVars.headerHeight) / 2)) / halfHeight
      // 1 : halfHeight = x : halfHeight - 200

      this._fixedTopConceptStones.forEach((stone, index) => {
        let x = leftSide + (spacing * index)

        let pos = new THREE.Vector3(x, y, 0.9).unproject(this._totemCamera)
        this._totemCamera.worldToLocal(pos)
        stone.elasticPosition.x = pos.x
        stone.elasticPosition.y = pos.y
        stone.elasticPosition.z = pos.z
      })
    },
    calculateFixedCenteredConceptStone: function () {
      // position fixed center
      // let trackWidth = this.viewPort.width / 2
      let excursion = this.importanceSelectionStoneTrackWidth / this.viewPort.width
      let imp = (this._fixedCenteredConceptStone.config.importance - 0.5) * 2
      this._fixedCenteredConceptStone.setScale(false, false)
      // let dist = -(this._fixedCenteredConceptStone.config.importance - 0.5) * 0.2
      // let pos = new THREE.Vector3(imp * excursion, 0, 0.5 + dist).unproject(this._totemCamera)
      let pos = new THREE.Vector3(imp * excursion, this.importanceSelectionStoneTrackY, 0.7).unproject(this._totemCamera)
      this._totemCamera.worldToLocal(pos)
      this._fixedCenteredConceptStone.position.copy(pos)
    },

    updateSelectedConcepts: function (value) {
      //
      // remove fixed centered concept stone if not selected anymore (remove button)
      if (this._fixedCenteredConceptStone && this.selectedConcepts.indexOf(this._fixedCenteredConceptStone.config) === -1) {
        this._totemCamera.remove(this._fixedCenteredConceptStone)
        this._fixedCenteredConceptStone.destroy()
        this._fixedCenteredConceptStone = null
      }

      // remove top stones if not selected anymore (from wheel)
      for (let i = this._fixedTopConceptStones.length - 1; i >= 0; i--) {
        let stone = this._fixedTopConceptStones[i]
        if (value.indexOf(stone.config) === -1) {
          this._fixedTopConceptStones.splice(i, 1)
          this._totemCamera.remove(stone)
          stone.destroy()
          this.calculateFixedTopConceptStones()
        }
      }

      // loop all selected concepts
      value.some((concept) => {
        if (this._fixedCenteredConceptStone && this._fixedCenteredConceptStone.config === concept) {
          // current unconfirmed stone is fixed centered stone
          // already configuring this
          // stop
          return true
        }

        // check if concept is not confirmed
        if (!concept.confirmed) {
          // search for concept in top stones
          let index = findIndex(this._fixedTopConceptStones, {config: concept})

          if (index === -1) {
            // concept is not confirmed, create a concept stone and put in the middle of the screen
            this._fixedCenteredConceptStone = new HopeConceptStone(concept, new THREE.MeshToonMaterial({
              specular: new THREE.Color(0x000000),
              shininess: 0,
              map: new HopeStoneTexture(concept.categoryObject.color, 1),
              transparent: false
            }))
            this._fixedCenteredConceptStone.material.onBeforeCompile = applyToonLighting
          } else {
            // stone was already selected, re-use it
            this._fixedCenteredConceptStone = this._fixedTopConceptStones[index]
            this._fixedTopConceptStones.splice(index, 1)
            this.calculateFixedTopConceptStones()
          }
          this._fixedCenteredConceptStone.elasticPosition.speed = 4
          this._fixedCenteredConceptStone.setVisible(true)
          this._fixedCenteredConceptStone.castShadow = false
          this._totemCamera.add(this._fixedCenteredConceptStone)
          this.onResize()
          // stop here
          return true
        }
        return false
      })
    },
    onResize: function (animate = false) {
      if (this.blockedResize) {
        return
      }

      if (this._renderer) {
        this._renderer.setPixelRatio(window.devicePixelRatio)
        this._renderer.setSize(this.canvasWidth, this.viewPort.height)
      }
      if (!animate) {
        if (this._totemCamera) {
          this._totemCamera.aspect = this.canvasWidth / this.viewPort.height
          this._totemCamera.updateProjectionMatrix()
        }
      } else {
        let animDummy = {
          width: this.$refs.threeCanvas.width,
          height: this.$refs.threeCanvas.height
        }
        this.animatingWidth = true
        anime({
          targets: animDummy,
          width: this.canvasWidth,
          duration: 500,
          ease: 'easeInOutQuad',
          update: (anim) => {
            // this._renderer.setSize(anim.animations[0].currentValue, this.viewPort.height)
            this._totemCamera.aspect = anim.animations[0].currentValue / this.viewPort.height
            this._totemCamera.updateProjectionMatrix()
          },
          complete: () => {
            // this._renderer.setSize(this.canvasWidth, this.viewPort.height)
            this.animatingWidth = false
          }
        })
      }

      if (this._wheelFadePlane) {
        // let z = this.viewPort.height / this.viewPort.width
        // console.log(z)
        let pos = new THREE.Vector3(0, -1, 0.57).unproject(this._totemCamera)
        this._wheelScene.worldToLocal(pos)
        this._wheelFadePlane.position.y = pos.y
        // this._wheelFadePlane.position.z = pos.z
      }

      this.calculateFixedTopConceptStones()

      if (this._fixedCenteredConceptStone) {
        this.calculateFixedCenteredConceptStone()
      }
    },
    recursiveDatasetSearch: function (element, datasetName) {
      if (element.dataset) {
        let dataset = JSON.parse(JSON.stringify(element.dataset))
        if (dataset[datasetName] === '') {
          return true
        }
      }
      let parent = element.parentElement || element.parentNode
      if (parent && parent !== document.body) {
        return this.recursiveDatasetSearch(parent, datasetName)
      }
      return false
    },

    getScreenshot: function (strMime, width = -1, height = -1, cb) {
      if (width === -1 || height === -1) {
        try {
          return this._renderer.domElement.toDataURL(strMime)
        } catch (e) {
          console.warn(e)
        }
      } else {
        let canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        let sx = 0
        let sy = 0
        let sWidth = this._renderer.domElement.width
        let sHeight = this._renderer.domElement.height

        let scale = 0.9

        let dWidth = this._renderer.domElement.width / window.devicePixelRatio * scale
        let dHeight = this._renderer.domElement.height / window.devicePixelRatio * scale
        let dx = -(dWidth - width) / 2
        let dy = -(dHeight - height) / 2

        let renderer = this._renderer.domElement

        let ctx = canvas.getContext('2d')

        ctx.fillStyle = cssVars.cBackground
        ctx.fillRect(0, 0, width, height)

        this._totems.forEach((totem) => {
          totem.label.disable()
        })

        var background = new Image()
        background.src = '/static/img/facebook-card-jpg.jpg'

        // Make sure the image is loaded first otherwise nothing will draw.
        background.onload = () => {
          ctx.drawImage(background, 0, 0)

          // replace here
          ctx.drawImage(renderer, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)

          try {
            return cb(canvas.toDataURL(strMime))
          } catch (e) {
            console.warn(e)
          }
        }
      }
    },
    saveScreenshot: function () {
      try {
        let strDownloadMime = 'image/octet-stream'
        var strMime = 'image/png'
        this.getScreenshot(strMime, this._renderer.domElement.width, this._renderer.domElement.height, (imgData) => {
          this.saveFile(imgData.replace(strMime, strDownloadMime), 'screen.png')
        })
      } catch (e) {
        console.log(e)
      }
    },

    saveFile: function (strData, filename) {
      var link = document.createElement('a')
      if (typeof link.download === 'string') {
        document.body.appendChild(link) // Firefox requires the link to be in the body
        link.download = filename
        link.href = strData
        link.click()
        document.body.removeChild(link) // remove the link when done
      } else {
        // location.replace(uri)
      }
    }
  },
  watch: {
    explodeTotem: function (value) {
      this._lastInteractionTime = this._clock.elapsedTime
      this.setTotemAllStonesExplodedState(value === 1, this.currentTotem)
    },
    multiTotem: function (value) {
      this._lastInteractionTime = this._clock.elapsedTime
    },
    // detailsPanelConcept: function (value) {
    //   this._lastInteractionTime = this._clock.elapsedTime
    // },
    currentTotem: function (value) {
      this._lastInteractionTime = this._clock.elapsedTime
      this.totems.forEach((totem) => {
        this.setTotemAllStonesExplodedState(false, totem)
      })
      // this.multiTotem = 0
      if (value.id !== this.totems[0].id) {
        this.$emit('update:multiTotem', 0)
      }
      this.elasticWheel.value = 0
      // console.log('current totem change')
    },
    // watch selected concepts and finds unconfirmed stones
    // and adds them to the center of the screen
    selectedConcepts: function (value) {
      this.updateSelectedConcepts(value)
    },
    viewPort: function () {
      this.onResize()
    },
    totems: function (value) {
      if (this.mode === 'totem' && !this.animatingTopStones) {
        this.updateTotems(value)
      }
    },
    wheelConcepts: function (value) {
      if (this.mode === 'wheel') {
        this.updateWheelConcepts(value)
      }

      // move fixed centered stone to top if it has become selected
      if (this._fixedCenteredConceptStone && this._fixedCenteredConceptStone.config.confirmed) {
        this._fixedTopConceptStones.push(this._fixedCenteredConceptStone)
        this._fixedCenteredConceptStone = null
        this.calculateFixedTopConceptStones()
      }
    },
    mode: function (value) {
      this.updateMode(value)
    }
  }
}
</script>

<style scoped lang="scss">
@import 'variables';




.three-canvas {
  user-select: none;
  -webkit-user-drag: none;
  z-index: $z-background;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &.in-foreground {
    z-index: $z-foreground;
    pointer-events: none;
  }

  @include breakpoint(#{$bp-sm}px) {
    transition: transform 0.4s $ease-in-out-quad;
    &.on-the-side {
      transform: translate(-25%, 0);
    }
  }
}

.pointer-cursor {
  cursor: pointer;
  &.in-foreground {
    pointer-events: all;
  }
}

</style>



// WEBPACK FOOTER //
// src/molecules/TotemCanvas.vue